import React, { useState, useEffect } from "react";
import {
  Calendar,
  User,
  FileText,
  Download,
  Eye,
  Filter,
  Search,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Minus,
  ChevronDown,
  FileSpreadsheet,
} from "lucide-react";
import { ChecklistResponse } from "./InspectionChecklist";
import axiosInstance from "../../common/axiosInstance";

interface InspectionRecord {
  id: string;
  assetId: string;
  attachments?: string[];
  inspectorName: string;
  inspectorEmail: string;
  inspectionDate: string;
  inspectionType: "routine" | "maintenance" | "emergency" | "compliance";
  overallStatus: "pass" | "fail" | "attention";
  overallComments: string;
  correctiveActions?: string;
  nextInspectionDate?: string;
  responses: ChecklistResponse[];
  createdAt: string;
  updatedAt?: string;
  isDraft: boolean;
  checklistName?: string;
  assetName?: string;
  assetLocation?: string;
  // hptTestDate?: string;
  // nextServiceDueDate?: string;
  // lastRefillDate?: string;
  // nextRefillDueDate?: string;
  // amcStatus?: string;
  // amcStartDate?: string;
  // amcEndDate?: string;
  // amcVendor?: string;
  // All asset-specific fields
  [key: string]: any; // Allow any asset-specific field
}

interface InspectionHistoryProps {
  assetId: string;
  onViewInspection?: (inspection: InspectionRecord) => void;
  onDownloadReport?: (inspection: InspectionRecord, format: "pdf" | "excel") => void;
  onDownloadAll?: () => void;
}

const InspectionHistory: React.FC<InspectionHistoryProps> = ({
  assetId,
  onViewInspection,
  onDownloadReport,
  onDownloadAll,
}) => {
  const [inspections, setInspections] = useState<InspectionRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [dateFilter, setDateFilter] = useState<string>("all");
  const [downloadMenuId, setDownloadMenuId] = useState<string | null>(null);

  useEffect(() => {
    fetchInspectionHistory();
  }, [assetId]);

  const fetchInspectionHistory = async () => {
    try {
      setLoading(true);

      // Fetch real inspection data from API
      const response = await axiosInstance.get(`/api/inspection-assessments`, {
        params: {
          assetId,
          limit: 100,
          sortBy: "inspectionDate",
          sortOrder: "desc",
        },
      });

      if (response.data.success) {
        const assessments = response.data.data?.assessments || [];
        // Transform assessments to match InspectionRecord interface
        const transformedInspections = assessments.map((assessment: any) => ({
          id: assessment._id || assessment.id,
          assetId: assessment.assetId,
          attachments: assessment.attachments || [],
          inspectorName: assessment.inspectorName,
          inspectorEmail: assessment.inspectorEmail,
          inspectionDate: assessment.inspectionDate,
          inspectionType: assessment.inspectionType,
          overallStatus: assessment.overallStatus,
          overallComments: assessment.overallComments || "",
          correctiveActions: assessment.correctiveActions,
          nextInspectionDate: assessment.nextInspectionDate,
          responses: assessment.responses || [],
          createdAt: assessment.createdAt,
          updatedAt: assessment.updatedAt,
          isDraft: assessment.status === "draft",
          checklistName: assessment.checklistName,
          assetName: assessment.assetName,
          assetLocation: assessment.assetLocation,
          ///asset-specific fields
          hptTestDate: assessment.hptTestDate,
          nextServiceDueDate: assessment.nextServiceDueDate,
          lastRefillDate: assessment.lastRefillDate,
          nextRefillDueDate: assessment.nextRefillDueDate,
          amcStatus: assessment.amcStatus,
          amcStartDate: assessment.amcStartDate,
          amcEndDate: assessment.amcEndDate,
          amcVendor: assessment.amcVendor,
        }));
        setInspections(transformedInspections);
      } else {
        console.error("Failed to fetch inspections");
        setInspections([]);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching inspection history:", error);
    }
  };

  // Fallback to mock data for development
  // const fetchInspectionHistoryMock = async () => {
  //   try {
  //     setLoading(true);
  //     // Mock data for development
  //     const mockInspections: InspectionRecord[] = [
  //       {
  //         id: '1',
  //         assetId,
  //         inspectorName: 'John Smith',
  //         inspectorEmail: 'john.smith@company.com',
  //         inspectionDate: '2024-10-01',
  //         inspectionType: 'routine',
  //         overallStatus: 'pass',
  //         overallComments: 'All systems functioning normally. No issues found.',
  //         responses: [],
  //         createdAt: '2024-10-01T10:30:00Z',
  //         isDraft: false
  //       },
  //       {
  //         id: '2',
  //         assetId,
  //         inspectorName: 'Sarah Johnson',
  //         inspectorEmail: 'sarah.johnson@company.com',
  //         inspectionDate: '2024-09-15',
  //         inspectionType: 'maintenance',
  //         overallStatus: 'attention',
  //         overallComments: 'Minor wear observed on safety pin. Scheduled for replacement.',
  //         correctiveActions: 'Replace safety pin within 7 days',
  //         nextInspectionDate: '2024-10-15',
  //         responses: [],
  //         createdAt: '2024-09-15T14:20:00Z',
  //         isDraft: false
  //       },
  //       {
  //         id: '3',
  //         assetId,
  //         inspectorName: 'Mike Wilson',
  //         inspectorEmail: 'mike.wilson@company.com',
  //         inspectionDate: '2024-08-30',
  //         inspectionType: 'compliance',
  //         overallStatus: 'fail',
  //         overallComments: 'Pressure gauge reading below acceptable range. Equipment requires immediate attention.',
  //         correctiveActions: 'Replace pressure gauge and recalibrate system. Do not use until repaired.',
  //         responses: [],
  //         createdAt: '2024-08-30T09:15:00Z',
  //         isDraft: false
  //       },
  //       {
  //         id: '4',
  //         assetId,
  //         inspectorName: 'Emily Davis',
  //         inspectorEmail: 'emily.davis@company.com',
  //         inspectionDate: '2024-10-03',
  //         inspectionType: 'routine',
  //         overallStatus: 'attention',
  //         overallComments: 'Draft inspection - needs completion',
  //         responses: [],
  //         createdAt: '2024-10-03T16:45:00Z',
  //         isDraft: true
  //       }
  //     ];

  //     setInspections(mockInspections);
  //   } catch (error) {
  //     console.error('Error fetching inspection history:', error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const filteredInspections = inspections.filter((inspection) => {
    const matchesSearch =
      inspection.inspectorName
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      inspection.overallComments
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || inspection.overallStatus === statusFilter;
    const matchesType =
      typeFilter === "all" || inspection.inspectionType === typeFilter;

    let matchesDate = true;
    if (dateFilter !== "all") {
      const inspectionDate = new Date(inspection.inspectionDate);
      const now = new Date();

      switch (dateFilter) {
        case "week":
          matchesDate =
            now.getTime() - inspectionDate.getTime() <= 7 * 24 * 60 * 60 * 1000;
          break;
        case "month":
          matchesDate =
            now.getTime() - inspectionDate.getTime() <=
            30 * 24 * 60 * 60 * 1000;
          break;
        case "quarter":
          matchesDate =
            now.getTime() - inspectionDate.getTime() <=
            90 * 24 * 60 * 60 * 1000;
          break;
      }
    }

    return matchesSearch && matchesStatus && matchesType && matchesDate;
  });

  const getStatusBadge = (status: string, isDraft: boolean) => {
    if (isDraft) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
          Draft
        </span>
      );
    }

    switch (status) {
      case "pass":
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
            Pass
          </span>
        );
      case "fail":
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
            Fail
          </span>
        );
      case "attention":
        return (
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
            Attention
          </span>
        );
      default:
        return null;
    }
  };

  const getTypeBadge = (type: string) => {
    const colors = {
      routine: "bg-blue-100 text-blue-800",
      maintenance: "bg-purple-100 text-purple-800",
      emergency: "bg-red-100 text-red-800",
      compliance: "bg-green-100 text-green-800",
    };

    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${colors[type as keyof typeof colors] || "bg-gray-100 text-gray-800"
          }`}
      >
        {type.charAt(0).toUpperCase() + type.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const calculateComplianceStats = (responses: ChecklistResponse[]) => {
    if (!responses || !Array.isArray(responses)) {
      return {
        total: 0,
        totalWithNA: 0,
        complied: 0,
        partiallyComplied: 0,
        notComplied: 0,
        notApplicable: 0,
        compliancePercentage: 0,
      };
    }

    const totalWithNA = responses.length;
    const total = responses.filter((r) => r.status && r.status !== "na").length;
    const complied = responses.filter((r) => r.status?.toLowerCase() === "pass").length;
    const partiallyComplied = responses.filter((r) => r.status?.toLowerCase() === "attention").length;
    const notComplied = responses.filter((r) => r.status?.toLowerCase() === "fail").length;
    const notApplicable = responses.filter((r) => r.status?.toLowerCase() === "na").length;
    const compliancePercentage = total > 0 ? Math.round((complied / total) * 100) : 0;

    return {
      total,
      totalWithNA,
      complied,
      partiallyComplied,
      notComplied,
      notApplicable,
      compliancePercentage,
    };
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">
          Inspection History
        </h2>
        <div className="flex items-center gap-3">
          {/* Download All Reports button disabled as per requirement */}
          {/* {onDownloadAll && inspections.length > 0 && (
            <button
              onClick={onDownloadAll}
              className="flex items-center gap-2 px-4 py-2 bg-[#00A8A5] text-white rounded-lg hover:bg-[#008A87] transition-colors text-sm font-medium"
            >
              <Download size={16} />
              Download All Reports
            </button>
          )} */}
          <span className="text-sm text-gray-500">
            {filteredInspections.length} of {inspections.length} inspections
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search
                size={20}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Search by inspector or comments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="all">All Status</option>
            <option value="pass">Pass</option>
            <option value="fail">Fail</option>
            <option value="attention">Attention</option>
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="all">All Types</option>
            <option value="routine">Routine</option>
            <option value="maintenance">Maintenance</option>
            <option value="emergency">Emergency</option>
            <option value="compliance">Compliance</option>
          </select>

          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2"
          >
            <option value="all">All Time</option>
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
            <option value="quarter">Last Quarter</option>
          </select>
        </div>
      </div>

      {/* Inspection List */}
      <div className="space-y-4">
        {filteredInspections.length === 0 ? (
          <div className="text-center py-12">
            <FileText size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">
              No Inspections Found
            </h3>
            <p className="text-gray-500">
              {inspections.length === 0
                ? "No inspections have been recorded for this asset yet."
                : "No inspections match your current filters."}
            </p>
          </div>
        ) : (
          filteredInspections.map((inspection) => (
            <div
              key={inspection.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    {getStatusBadge(
                      inspection.overallStatus,
                      inspection.isDraft
                    )}
                    {getTypeBadge(inspection.inspectionType)}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {onViewInspection && (
                    <button
                      onClick={() => onViewInspection(inspection)}
                      className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye size={16} />
                    </button>
                  )}

                  {!inspection.isDraft && onDownloadReport && (
                    <div className="flex items-center gap-1">
                      {downloadMenuId === inspection.id ? (
                        <div className="flex bg-gray-50 border border-gray-200 rounded-lg p-1">
                          <button
                            onClick={() => {
                              setDownloadMenuId(null);
                              onDownloadReport(inspection, "pdf");
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-white hover:shadow-sm rounded transition-all flex items-center gap-1.5"
                          >
                            <FileText size={14} /> PDF
                          </button>
                          <div className="w-[1px] bg-gray-200 mx-1"></div>
                          <button
                            onClick={() => {
                              setDownloadMenuId(null);
                              onDownloadReport(inspection, "excel");
                            }}
                            className="px-3 py-1.5 text-xs font-medium text-green-600 hover:bg-white hover:shadow-sm rounded transition-all flex items-center gap-1.5"
                          >
                            <FileSpreadsheet size={14} /> Excel
                          </button>
                          <div className="w-[1px] bg-gray-200 mx-1"></div>
                          <button
                            onClick={() => setDownloadMenuId(null)}
                            className="px-2 py-1.5 text-xs font-medium text-gray-500 hover:bg-white hover:shadow-sm rounded transition-all"
                            title="Cancel"
                          >
                            <XCircle size={14} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDownloadMenuId(inspection.id)}
                          className="p-2 text-gray-500 hover:text-[#00A8A5] hover:bg-[#00f7f31a] rounded-lg transition-colors flex items-center gap-1"
                          title="Download Report"
                        >
                          <Download size={16} />
                          <ChevronDown size={12} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Checklist Name */}
              {inspection.checklistName && (
                <div className="mb-3">
                  <p className="text-sm font-medium text-gray-700">
                    Checklist:{" "}
                    <span className="text-[#00A8A8]">
                      {inspection.checklistName}
                    </span>
                  </p>
                </div>
              )}

              {/* Compliance Statistics */}
              {!inspection.isDraft && inspection.responses && inspection.responses.length > 0 && (
                <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-gray-700">Question Compliance</h4>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${calculateComplianceStats(inspection.responses).compliancePercentage >= 80
                      ? 'bg-green-100 text-green-800'
                      : calculateComplianceStats(inspection.responses).compliancePercentage >= 50
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                      }`}>
                      {calculateComplianceStats(inspection.responses).compliancePercentage}%
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <FileText className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Total</p>
                        <p className="text-sm font-semibold text-gray-800">
                          {calculateComplianceStats(inspection.responses).totalWithNA}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Complied</p>
                        <p className="text-sm font-semibold text-green-700">
                          {calculateComplianceStats(inspection.responses).complied}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                        <AlertTriangle className="w-4 h-4 text-yellow-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Partially</p>
                        <p className="text-sm font-semibold text-yellow-700">
                          {calculateComplianceStats(inspection.responses).partiallyComplied}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                        <XCircle className="w-4 h-4 text-red-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Not Complied</p>
                        <p className="text-sm font-semibold text-red-700">
                          {calculateComplianceStats(inspection.responses).notComplied}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                        <Minus className="w-4 h-4 text-slate-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Not Applicable</p>
                        <p className="text-sm font-semibold text-slate-700">
                          {calculateComplianceStats(inspection.responses).notApplicable}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Inspection Date</p>
                    <p className="font-medium">
                      {formatDate(inspection.inspectionDate)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <User size={16} className="text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Inspector</p>
                    <p className="font-medium">{inspection.inspectorName}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Created</p>
                    <p className="font-medium">
                      {formatDateTime(inspection.createdAt)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <p className="text-sm text-gray-500 mb-1">Comments</p>
                <p className="text-gray-800">
                  {inspection.overallComments || "No comments provided"}
                </p>
              </div>

              {inspection.correctiveActions && (
                <div className="mb-3">
                  <p className="text-sm text-gray-500 mb-1">
                    Corrective Actions
                  </p>
                  <p className="text-orange-800 bg-orange-50 p-2 rounded text-sm">
                    {inspection.correctiveActions}
                  </p>
                </div>
              )}

              {inspection.nextInspectionDate && (
                <div className="text-sm text-gray-500">
                  Next inspection due:{" "}
                  <span className="font-medium">
                    {formatDate(inspection.nextInspectionDate)}
                  </span>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default InspectionHistory;
